/**
 * The command center's period: its first and last day (both inclusive, UTC
 * `YYYY-MM-DD`), and the preset it came from, if any. The server takes the
 * two days and compares with the same number of days immediately before.
 *
 * Pure: presets, labels and the chart choices that depend on a period's
 * length are written once here and tested.
 *
 * @module components/commandCenter/commandCenterPeriod
 */

const DAY_MS = 86_400_000;

export const PERIOD_PRESETS = [
  { id: "7d", label: "Last 7 days", previous: "previous 7 days" },
  { id: "30d", label: "Last 30 days", previous: "previous 30 days" },
  { id: "3m", label: "Last 3 months", previous: "previous 3 months" },
  { id: "6m", label: "Last 6 months", previous: "previous 6 months" },
  { id: "ytd", label: "Year to date", previous: null },
  { id: "12m", label: "Last 12 months", previous: "previous 12 months" },
] as const;
export type PeriodPresetId = (typeof PERIOD_PRESETS)[number]["id"];

/** Days a rolling preset covers, today included; six months matches the server's 26 weeks. */
const PRESET_DAYS: Readonly<Record<Exclude<PeriodPresetId, "ytd">, number>> = {
  "7d": 7,
  "30d": 30,
  "3m": 91,
  "6m": 182,
  "12m": 365,
};

/** The longest period the server accepts. */
export const MAX_PERIOD_DAYS = 731;

export interface CommandCenterPeriod {
  readonly from: string;
  readonly to: string;
  readonly preset: PeriodPresetId | null;
}

export const dayKeyOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const dayMs = (day: string): number => Date.parse(`${day}T00:00:00Z`);

/** Today, as the server counts it (UTC). */
export const todayKey = (nowMs: number = Date.now()): string => dayKeyOf(nowMs);

export function presetPeriod(id: PeriodPresetId, today: string): CommandCenterPeriod {
  const to = today;
  if (id === "ytd") return { from: `${today.slice(0, 4)}-01-01`, to, preset: id };
  return { from: dayKeyOf(dayMs(today) - (PRESET_DAYS[id] - 1) * DAY_MS), to, preset: id };
}

/** Days in a period, both ends included. */
export const periodDays = (period: Pick<CommandCenterPeriod, "from" | "to">): number =>
  Math.round((dayMs(period.to) - dayMs(period.from)) / DAY_MS) + 1;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "13 Apr 2026" */
export function formatLongDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()] ?? ""} ${date.getUTCFullYear()}`;
}

/** The picker's label: "13 Apr 2026 – 11 Oct 2026", or the one day. */
export function periodLabel(period: Pick<CommandCenterPeriod, "from" | "to">): string {
  return period.from === period.to
    ? formatLongDay(period.from)
    : `${formatLongDay(period.from)} – ${formatLongDay(period.to)}`;
}

/** What the comparison is called: "previous 6 months", or "previous 42 days". */
export function previousPeriodLabel(period: CommandCenterPeriod): string {
  const preset = PERIOD_PRESETS.find((entry) => entry.id === period.preset);
  if (preset?.previous) return preset.previous;
  const days = periodDays(period);
  return days === 1 ? "previous day" : `previous ${days} days`;
}

/** The period in a sentence: "the last 6 months", "this year so far", or its dates. */
export function describePeriod(period: CommandCenterPeriod): string {
  switch (period.preset) {
    case null:
      return periodLabel(period);
    case "ytd":
      return "this year so far";
    default: {
      const label = PERIOD_PRESETS.find((entry) => entry.id === period.preset)?.label ?? "";
      return `the ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
    }
  }
}

/** Daily figures are averaged over this many days so a long chart reads as a trend. */
export function smoothingFor(days: number): number {
  if (days <= 14) return 1;
  if (days <= 62) return 7;
  return 14;
}

const orderDays = (a: string, b: string): [string, string] => (a <= b ? [a, b] : [b, a]);

/** The period a picker selection asks for, or why it cannot be asked for. */
export function selectionPeriod(
  from: string | null,
  to: string | null,
  today: string,
):
  | { readonly ok: true; readonly period: CommandCenterPeriod }
  | { readonly ok: false; readonly reason: string } {
  if (from === null) return { ok: false, reason: "Choose a first day." };
  const last = to ?? from;
  const [first, end] = orderDays(from, last);
  if (end > today) return { ok: false, reason: "Choose days up to today." };
  const period = { from: first, to: end, preset: null };
  if (periodDays(period) > MAX_PERIOD_DAYS)
    return { ok: false, reason: "Choose two years or less." };
  return { ok: true, period };
}

/** The preset a period still matches today, so a reopened picker shows it as chosen. */
export function matchingPreset(
  period: Pick<CommandCenterPeriod, "from" | "to">,
  today: string,
): PeriodPresetId | null {
  for (const preset of PERIOD_PRESETS) {
    const candidate = presetPeriod(preset.id, today);
    if (candidate.from === period.from && candidate.to === period.to) return preset.id;
  }
  return null;
}
